/**
 * ExternalPostsPanel — "Posted elsewhere": the Facebook Marketplace / Craigslist posts recorded for
 * a listing, with Open post, Mark removed and Delete (BR-15, BR-17, BR-18).
 *
 * Presentational: the owner supplies the posts and the mutation callbacks.
 */
import { useState } from 'react';
import { AlertTriangle, MoreHorizontal } from 'lucide-react';
import { asIsoDateTimeString, formatTimestamp } from '@resida/shared';
import { LISTING_SITE_LABELS, type ExternalListingPostDto } from '../../lib/external-listing-posts.api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { buttonVariants } from '../ui/button-variants';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { ConfirmDialog } from '../ConfirmDialog';
import { EmptyState } from '../layout';

interface ExternalPostsPanelProps {
  posts: ExternalListingPostDto[];
  /** BR-27/BR-17: a non-active listing with posted posts needs the landlord to take them down. */
  listingActive: boolean;
  /** Listing status word for the takedown sentence ("closed", "paused", …). */
  listingStatusLabel: string;
  /** Property timezone for the post dates; dates are omitted until it is known. */
  timezone: string | undefined;
  /** Id of the post with a mutation in flight, if any. */
  busyPostId: string | null;
  onMarkRemoved: (postId: string) => void;
  onDelete: (postId: string) => void;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

export function ExternalPostsPanel({
  posts,
  listingActive,
  listingStatusLabel,
  timezone,
  busyPostId,
  onMarkRemoved,
  onDelete,
}: ExternalPostsPanelProps) {
  const [deleteTarget, setDeleteTarget] = useState<ExternalListingPostDto | null>(null);

  const formatDay = (iso: string) =>
    timezone ? formatTimestamp(asIsoDateTimeString(iso), timezone, DATE_OPTIONS) : '';

  if (posts.length === 0) {
    return (
      <EmptyState
        title="Not posted anywhere else yet"
        description="Posted from your phone? Add the post's link so Resida can remind you to take it down later."
        className="py-6"
        data-testid="external-posts-empty"
      />
    );
  }

  const postedCount = posts.filter((p) => p.status === 'posted').length;
  const showTakedownBanner = !listingActive && postedCount > 0;

  const describe = (post: ExternalListingPostDto) => {
    const parts = [
      ['Posted', formatDay(post.postedAt)].filter(Boolean).join(' '),
      ...(post.removedAt && formatDay(post.removedAt) ? [`removed ${formatDay(post.removedAt)}`] : []),
      post.source === 'extension' ? 'recorded by the extension' : 'added by you',
    ];
    return parts.join(' · ');
  };

  return (
    <div className="space-y-3">
      {showTakedownBanner && (
        <div
          role="alert"
          data-testid="takedown-banner"
          className="flex items-start gap-3 rounded-md border border-warning/25 bg-warning/10 p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-warning" aria-hidden="true" />
          <div className="space-y-0.5">
            <p className="font-semibold text-warning">Your posts on other sites are still live.</p>
            <p className="text-foreground">
              This listing is {listingStatusLabel}, but Resida cannot pause them. Delete each one on its site, then mark
              it removed here so the reminder clears.
            </p>
          </div>
        </div>
      )}

      <h2 className="text-base font-semibold text-foreground">Posted elsewhere</h2>
      <ul data-testid="external-posts" className="divide-y divide-border rounded-md border border-border">
        {posts.map((post) => {
          const siteLabel = LISTING_SITE_LABELS[post.site];
          const posted = post.status === 'posted';
          const busy = busyPostId === post.id;
          return (
            <li
              key={post.id}
              data-testid="external-post-row"
              className="flex flex-col gap-3 p-3 md:flex-row md:items-center"
            >
              <div className="flex items-start justify-between gap-2 md:min-w-0 md:flex-1">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{siteLabel}</p>
                  <p className="text-xs text-muted-foreground">{describe(post)}</p>
                </div>
                <Badge
                  variant={!posted ? 'secondary' : listingActive ? 'success' : 'warning'}
                  data-testid="external-post-status"
                >
                  {!posted ? 'Removed' : listingActive ? 'Live' : 'Still live'}
                </Badge>
              </div>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <a
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-post"
                  className={buttonVariants({ variant: 'outline' })}
                >
                  Open post ↗
                </a>
                {posted && (
                  <Button
                    type="button"
                    variant={listingActive ? 'outline' : 'default'}
                    disabled={busy}
                    onClick={() => onMarkRemoved(post.id)}
                    data-testid="mark-removed"
                  >
                    Mark removed
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-full gap-2 md:w-11 md:border-transparent md:px-0"
                      aria-label={`More actions for ${siteLabel} post`}
                      disabled={busy}
                      data-testid="external-post-menu"
                    >
                      <MoreHorizontal aria-hidden="true" />
                      <span className="md:hidden">More actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="min-h-[44px] text-destructive focus:text-destructive"
                      data-testid="delete-external-post"
                      onSelect={() => setDeleteTarget(post)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) onDelete(deleteTarget.id);
          setDeleteTarget(null);
        }}
        title="Delete this post record?"
        message="This removes the record from Resida only. It doesn't delete the post on the site."
        confirmText="Delete"
        isDestructive
      />
    </div>
  );
}
